import { useEffect, useState } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useNavigate } from "react-router-dom";
import Menu from "./Components/Menu";
import Header from "./Components/Header";
import { config, priceFormat, shortenUrl } from "./Components/Config";
import { format, addMonths, subDays } from "date-fns";

const SalesOrderDetail = () => {
  const { id_so } = useParams();
  const [ salesOrder, setSalesOrder ] = useState(null);
  const [ salesOrderItemList, setSalesOrderItemList ] = useState(null);
  const [ salesOrderCustomerId, setSalesOrderCustomerId ] = useState('');
  const [ salesOrderDomain, setSalesOrderDomain ] = useState('');
  const [ salesOrderScreenshot, setSalesOrderScreenshot ] = useState(null);
  const [ salesOrderNotes, setSalesOrderNotes ] = useState('');
  const [ salesOrderType, setSalesOrderType ] = useState('General');
  const [ quotation, setQuotation ] = useState(null);
  const [ quotationItems, setQuotationItems ] = useState(null);
  const [ paymentSimulations, setPaymentSimulations ] = useState(null);
  const [ iouList, setIouList ] = useState(null);
  const [ activationDate, setActivationDate ] = useState('');
  const [ isEditable, setIsEditable ] = useState(true);
  const [ isEditing, setIsEditing ] = useState(true);
  const [ isLoadingSubmit, setIsLoadingSubmit ] = useState(false);

  const navigate = useNavigate();

  const crmUser = JSON.parse(localStorage.getItem('crmUser'));

  useEffect(() => {
    if (id_so) {
      getSalesOrderDetail();
      getSalesOrderItems();
      getSalesOrderItemList();
      getIou();
    }
  }, [id_so]);

  useEffect(() => {
    if (salesOrder) {
      getQuotation();
      if (salesOrder.so_status === 'Completed') {
        setIsEditable(false);
      }
    }
  }, [salesOrder]);

  useEffect(() => {
    if (quotation) {
      getPaymentList();
    }
  }, [quotation]);

  useEffect(() => {
    if (isEditing) {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }, [isEditing]);

  const getSalesOrderDetail = async () => {
    try {
      const fetchSalesOrder = await jwtFetch(config.ENDPOINT + 'get/so', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_so: id_so
        })
      });
      const responseSalesOrder = await fetchSalesOrder.json();
      if (responseSalesOrder.status === 200) {
        setSalesOrder(responseSalesOrder.data[0]);
        setSalesOrderNotes(responseSalesOrder.data[0].so_notes);
        if (responseSalesOrder.data[0].so_customer_id) {
          setSalesOrderCustomerId(responseSalesOrder.data[0].so_customer_id);
        }
        if (responseSalesOrder.data[0].so_domain) {
          setSalesOrderDomain(responseSalesOrder.data[0].so_domain);
        }
      } else {
        alert(responseSalesOrder.message);
      }
    } catch (error) {
      console.error('Error in getSalesOrderDetail:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getSalesOrderItems = async () => {
    try {
      const fetchGetSalesOrderItems = await jwtFetch(config.ENDPOINT + 'get/so_item', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          so_id: id_so
        })
      });
      const responseGetSalesOrderItems = await fetchGetSalesOrderItems.json();
      if (responseGetSalesOrderItems.status === 200) {
        if (responseGetSalesOrderItems.data[0].product_active_licence_date) {
          setActivationDate(format(new Date(responseGetSalesOrderItems.data[0].product_active_licence_date), 'yyyy-MM-dd'));
          setIsEditable(true);
          setIsEditing(false);
        }
      } else {
        alert(responseGetSalesOrderItems.message);
      }
    } catch (error) {
      console.error('Error in getSalesOrderItems:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getSalesOrderItemList = async () => {
    try {
      const fetchGetSalesOrderItemList = await jwtFetch(config.ENDPOINT + 'get/so_item_list', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          so_id: id_so
        })
      });
      const responseGetSalesOrderItemList = await fetchGetSalesOrderItemList.json();
      if (responseGetSalesOrderItemList.status === 200) {
        if (responseGetSalesOrderItemList.data.length > 0) {
          for (let i in responseGetSalesOrderItemList.data) {
            if (responseGetSalesOrderItemList.data[i].product_type === 'Subscription') {
              setSalesOrderType('Subscription');
            }
          }
        }
        setSalesOrderItemList(responseGetSalesOrderItemList.data);
        if (responseGetSalesOrderItemList.data[0].product_active_licence_date) {
          setActivationDate(format(new Date(responseGetSalesOrderItemList.data[0].product_active_licence_date), 'yyyy-MM-dd'));
          setIsEditable(true);
          setIsEditing(false);
        }
      } else {
        alert(responseGetSalesOrderItemList.message);
      }
    } catch (error) {
      console.error('Error in getSalesOrderItemList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getSalesOrderItemProduct = async (company_id, product_id) => {
    try {
      const fetchSalesOrderItemProduct = await jwtFetch(config.ENDPOINT + 'get/so_item_list', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          company_id: company_id,
          product_id: product_id,
        })
      });
      const responseSalesOrderItemProduct = await fetchSalesOrderItemProduct.json();
      if (responseSalesOrderItemProduct.status === 200) {
        return responseSalesOrderItemProduct.data[0];
      } else {
        alert(responseSalesOrderItemProduct.message);
      }
    } catch (error) {
      console.error('Error in getSalesOrderItemProduct:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getQuotation = async () => {
    try {
      const fetchQuotation = await jwtFetch(config.ENDPOINT + 'get/quotation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: salesOrder.quotation_id
        })
      });
      const responseGetQuotation = await fetchQuotation.json();
      if (responseGetQuotation.status === 200) {
        setQuotation(responseGetQuotation.data[0]);
        let items = [];
        let itemList = [];
        for (let i in responseGetQuotation.data[0].quotation_items) {
          responseGetQuotation.data[0].quotation_items[i].companies = [{
            id_quotation_item: responseGetQuotation.data[0].quotation_items[i].quotation_item_id,
            company_id: responseGetQuotation.data[0].subcompany_id ? responseGetQuotation.data[0].subcompany_id : '',
            company_name: responseGetQuotation.data[0].subcompany_id ? responseGetQuotation.data[0].subcompany_name : '',
            product_qty: responseGetQuotation.data[0].quotation_items[i].product_qty,
            product_cost_original: responseGetQuotation.data[0].quotation_items[i].product_cost_original,
            product_cost: responseGetQuotation.data[0].quotation_items[i].product_cost,
            product_price_original: responseGetQuotation.data[0].quotation_items[i].product_price_original,
            product_price: responseGetQuotation.data[0].quotation_items[i].product_price,
            product_subtotal: responseGetQuotation.data[0].quotation_items[i].product_subtotal,
            product_discount_price: responseGetQuotation.data[0].quotation_items[i].product_discount_price,
            product_discount_rate: responseGetQuotation.data[0].quotation_items[i].product_discount_rate,
            product_discount_prorate: responseGetQuotation.data[0].quotation_items[i].product_discount_prorate,
            product_margin_price: responseGetQuotation.data[0].quotation_items[i].product_margin_price,
            product_margin_rate: responseGetQuotation.data[0].quotation_items[i].product_margin_rate,
            product_total: responseGetQuotation.data[0].quotation_items[i].product_total,
            product_grand_total: responseGetQuotation.data[0].quotation_items[i].product_grand_total,
            product_prorate: 100,
            contract_long_period: responseGetQuotation.data[0].quotation_items[i].contract_long_period,
          }];
          items.push(responseGetQuotation.data[0].quotation_items[i]);
          setQuotationItems([...items]);
        }
        for (let i in responseGetQuotation.data[0].quotation_item_list) {
          itemList.push(responseGetQuotation.data[0].quotation_item_list[i]);
          if (responseGetQuotation.data[0].quotation_item_list[i].service_type === 'Additional') {
            const responseItemProduct = await getSalesOrderItemProduct(responseGetQuotation.data[0].company_id, responseGetQuotation.data[0].quotation_item_list[i].product_id);
            if (responseItemProduct.so_customer_id) {
              setSalesOrderCustomerId(responseItemProduct.so_customer_id);
            }
            if (responseItemProduct.so_domain) {
              setSalesOrderDomain(responseItemProduct.so_domain);
            }
          }
        }
      } else {
        alert(responseGetQuotation.message);
      }
    } catch (error) {
      console.error('Error in getQuotation:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getQuotationItemAccount = async (quotationId, productId) => {
    try {
      const reqeustQuotationItemAccount = {
        quotation_id: quotationId,
        product_id: productId ? productId : '',
      };
      const fetchQuotationItemAccount = await jwtFetch(config.ENDPOINT + 'get/quotation_item_account', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reqeustQuotationItemAccount)
      });
      const responseQuotationItemAccount = await fetchQuotationItemAccount.json();
      if (responseQuotationItemAccount.status === 200) {
        return responseQuotationItemAccount.data;
      } else {
        alert(responseQuotationItemAccount.message);
        return;
      }
    } catch (error) {
      console.error('Error in getQuotationItemAccount:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getIou = async () => {
    try {
      const fetchIou = await jwtFetch(config.ENDPOINT + 'get/iou', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          so_id: id_so
        })
      });
      const responseIou = await fetchIou.json();
      if (responseIou.status === 200) {
        for (let i in responseIou.data) {
          if (responseIou.data[i].iou_items) {
            responseIou.data[i].iou_items = await getIouItemList(responseIou.data[i].id_iou);
          }
        }
        setIouList(responseIou.data);
      } else {
        alert(responseIou.message);
      }
    } catch (error) {
      console.error('Error in getIou:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getIouItemList = async (iou_id) => {
    try {
      const requestIouList = {
        iou_id: iou_id
      }
      const fetchIouList = await jwtFetch(config.ENDPOINT + 'get/iou_item', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestIouList)
      });
      const responseIouList = await fetchIouList.json();
      if (responseIouList.status === 200) {
        const newIouItems = [];
        for (let i in responseIouList.data) {
          let newIouItem = responseIouList.data[i];
          const companyLicence = await getCompanyLicence(responseIouList.data[i].company_id, responseIouList.data[i].product_id, responseIouList.data[i].product_domain);
          if (companyLicence) {
            newIouItem.licence_start_period = companyLicence.licence_start_period;
            newIouItem.licence_end_period = companyLicence.licence_end_period;
            newIouItem.action = 'Update';
          }
          newIouItems.push(newIouItem);
        }
        return newIouItems;
      } else {
        alert(responseIouList.message);
      }
    } catch (error) {
      console.error('Error in getIouItemList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const getPaymentList = async () => {
    try {
      const fetchPaymentSimulations = await jwtFetch(config.ENDPOINT + 'get/quotation_payment_simulation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          quotation_id: quotation.id_quotation
        })
      });
      const responsePaymentSimulations = await fetchPaymentSimulations.json();
      if (responsePaymentSimulations.status === 200) {
        setPaymentSimulations(responsePaymentSimulations.data);
      } else {
        alert(responsePaymentSimulations.message);
      }
    } catch (error) {
      console.error('Error in getPaymentList:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getQuotationPaymentSimulation = async (quotationId) => {
    try {
      const fetchQuotationPaymentSimulation = await jwtFetch(config.ENDPOINT + 'get/quotation_payment_simulation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          quotation_id: quotationId
        })
      });
      const responseQuotationPaymentSimulation = await fetchQuotationPaymentSimulation.json();
      if (responseQuotationPaymentSimulation.status === 200) {
        return responseQuotationPaymentSimulation.data;
      } else {
        alert(responseQuotationPaymentSimulation.message);
        return;
      }
    } catch (error) {
      console.error('Error in getQuotationPaymentSimulation:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getCompanyLicence = async (companyId, productId, licenceDomain) => {
    try {
      const requestCompanyLicence = {
        "company_id": companyId,
        "product_id": productId,
        "licence_domain": licenceDomain
      }
      const fetchCompanyLicence = await jwtFetch(config.ENDPOINT + 'get/clients_company_licence', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestCompanyLicence)
      });
      const responseCompanyLicence = await fetchCompanyLicence.json();
      if (responseCompanyLicence.status === 200) {
        if (responseCompanyLicence.data.length > 0) {
          return responseCompanyLicence.data[0];
        } else {
          return null;
        }
      } else {
        alert(responseCompanyLicence.message);
      }
    } catch (error) {
      console.error('Error in getCompanyLicence:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  } 

  const getProduct = async (productId) => {
    try {
      const requestProduct = {
        "id_product": productId
      }
      const fetchProduct = await jwtFetch(config.ENDPOINT + 'get/product', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestProduct)
      });
      const responseProduct = await fetchProduct.json();
      if (responseProduct.status === 200) {
        if (responseProduct.data.length > 0) {
          return responseProduct.data[0];
        } else {
          return null;
        }
      } else {
        alert(responseProduct.message);
      }
    } catch (error) {
      console.error('Error in getProduct:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  } 

  const copyItem = (itemIndex) => {
    let description = quotationItems[itemIndex].product_display_name;
    description += '\n\n';
    description += quotationItems[itemIndex].product_display_description;
    description += '\n\n';
    if (iouList) {
      description += iouList[0].service_type === 'Additional' ? 'Period - Current Active Subscription: '+ (format(new Date(iouList[0].iou_items[itemIndex].licence_start_period), 'd MMMM yyyy') + " - " + format(new Date(iouList[0].iou_items[itemIndex].licence_end_period), 'd MMMM yyyy')) + '\n' : '';
      description += 'Period - for this QT Order: '+ (format(new Date(iouList[0].iou_items[itemIndex].start_term_period), 'd MMMM yyyy') + " - " + format(new Date(iouList[0].iou_items[itemIndex].end_term_period), 'd MMMM yyyy')) + '\n';
    }
    navigator.clipboard.writeText(description);
  }
  
  const cancelStatus = async () => {
    try {
      setIsLoadingSubmit(true);
      const fetchEditQuotation = await jwtFetch(config.ENDPOINT + 'update/so_status', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_so: id_so,
          id_quotation: salesOrder.quotation_id,
          quotation_status: "Canceled",
          so_status: "Canceled",
        })
      });
      const responseEditQuotation = await fetchEditQuotation.json();
      if (responseEditQuotation.status === 200) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error in cancelStatus:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  };

  const editActivationDate = async (e) => {
    try {
      e.preventDefault();
      setIsLoadingSubmit(true);

      let salesOrderScreenshotData = null;
      if (salesOrderScreenshot) {
        const responseUploadScreenshot = await uploadScreenshot();
        if (responseUploadScreenshot) {
          salesOrderScreenshotData = JSON.stringify(responseUploadScreenshot);
        }
      }

      const crmUser = JSON.parse(localStorage.getItem('crmUser'));
      for (let i in salesOrderItemList) {
        const companyLicence = await getCompanyLicence(salesOrder.company_id, salesOrderItemList[i].product_id, salesOrderItemList[i].product_domain);      
        const product = await getProduct(salesOrderItemList[i].product_id);      
        let requestEditActivationDate = {
          id_so: id_so,
          so_item_id: salesOrderItemList[i].so_item_id,
          so_item_list_id: salesOrderItemList[i].id_so_item_list,
          so_customer_id: salesOrderCustomerId,
          so_domain: salesOrderDomain,
          so_screenshot: salesOrderScreenshotData,
          so_notes: salesOrderNotes,
          quotation_id: salesOrderItemList[i].quotation_id,
          quotation_item_list_id: salesOrderItemList[i].quotation_item_list_id,
          quotation_created_on: format(new Date(salesOrderItemList[i].quotation_created_on), 'yyyy-MM-dd HH:mm:ss'),
          quotation_created_by: salesOrderItemList[i].quotation_created_by,
          company_id: salesOrderItemList[i].company_id,
          subcompany_id: salesOrderItemList[i].subcompany_id,
          product_id: salesOrderItemList[i].product_id,
          product_type: product.product_type,
          product_qty_current: companyLicence ? companyLicence.licence_total_seat : 0,
          product_qty: salesOrderItemList[i].product_qty,
          product_currency: salesOrderItemList[i].product_currency_original,
          product_price: salesOrderItemList[i].product_price_original,
          product_subtotal: salesOrderItemList[i].product_subtotal,
          product_active_licence_date: format(new Date(activationDate), 'yyyy-MM-dd'),
          product_expired_licence_date: format(subDays(addMonths(new Date(activationDate), parseInt(salesOrderItemList[i].contract_long_period)), 1), 'yyyy-MM-dd'),
          contract_long_period: salesOrderItemList[i].contract_long_period,
          contract_duration_period: salesOrderItemList[i].contract_duration_period,
          billing_long_period: salesOrderItemList[i].billing_long_period,
          billing_duration_period: salesOrderItemList[i].billing_duration_period,
          billing_payment_term: salesOrderItemList[i].billing_payment_term,
          billing_payment_period: salesOrderItemList[i].billing_payment_period,
          service_type: salesOrderItemList[i].service_type,
          status: 'Completed',
          updated_by: crmUser.user_keyid,
          updated_on: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        }

        await jwtFetch(config.ENDPOINT + 'activate/so_item', {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestEditActivationDate)
        });
      }
      window.location.reload();
    } catch (error) {
      console.error('Error in editActivationDate:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  }
  
  const editStatusSalesOrder = async (status) => {
    try {
      setIsLoadingSubmit(true);

      const fetchEditSalesOrder = await jwtFetch(config.ENDPOINT + 'update/so', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_so: id_so,
          so_status: status,
          // so_status: 'In Progress',
        })
      });

      const responseEditSalesOrder = await fetchEditSalesOrder.json();
      if (responseEditSalesOrder.status === 200) {
        const crmUser = JSON.parse(localStorage.getItem('crmUser'));

        let iouItems = [];
        let terms = [];

        for (let i in salesOrderItemList) {
          let fetchGetTerms = null;
          if (salesOrderItemList[i].service_type === 'New Order') {
            fetchGetTerms = await jwtFetch(config.ENDPOINT + 'simulation/new_subscription', {
              method: 'POST',
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                  "item_name": salesOrderItemList[i].product_display_name,
                  "sku_price": salesOrderItemList[i].product_price,
                  "num_user": salesOrderItemList[i].product_qty,
                  "idr_usd_rate": salesOrder.so_exchange_rate,
                  "billing_start_date": format(activationDate, 'yyyy-MM-dd HH:mm:ss'),
                  "installment_type": salesOrderItemList[i].billing_payment_period,
                  "total_contract_month": salesOrderItemList[i].contract_long_period,
                  "quotation_tax_rate": 12,
                  "product_discount_price": salesOrderItemList[i].product_discount_price,
                  "product_discount_prorate": salesOrderItemList[i].product_discount_prorate,
                })
            });
          } else if (salesOrderItemList[i].service_type === 'Additional') {
            const companyLicense = await getCompanyLicence(salesOrder.company_id, salesOrderItemList[i].product_id, salesOrderItemList[i].product_domain);

            fetchGetTerms = await jwtFetch(config.ENDPOINT + 'simulation/additional_licence', {
              method: 'POST',
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                "subscription": {
                  "item_name": salesOrderItemList[i].product_display_name,
                  "sku_price": salesOrderItemList[i].product_price,
                  "num_user": salesOrderItemList[i].product_qty,
                  "product_discount_price": salesOrderItemList[i].product_discount_price,
                  "product_discount_prorate": salesOrderItemList[i].product_discount_prorate,
                  "billing_start_date": format(new Date(companyLicense.licence_start_period), 'dd MMM yyyy'),
                  "installment_type": companyLicense.billcycle_payment_period,
                  "total_contract_month": companyLicense.licence_long_period,
                  "quotation_tax_rate": 12,
                },
                "additionalLicences": {
                    "additional_licence_start_date": format(salesOrderItemList[i].product_active_licence_date, 'dd MMM yyyy'),
                    "additional_licence_sku_price": salesOrderItemList[i].product_price,
                    "additional_licence_num_user": salesOrderItemList[i].product_qty,
                    "additional_licence_idr_usd_rate": salesOrder.so_exchange_rate,
                    "additional_licence_product_discount_price": salesOrderItemList[i].product_discount_price,
                    "additional_licence_product_discount_prorate": salesOrderItemList[i].product_discount_prorate,
                }
              })
            });
          } else if (salesOrderItemList[i].service_type === 'Renewal') {
            fetchGetTerms = await jwtFetch(config.ENDPOINT + 'simulation/new_subscription', {
              method: 'POST',
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                  "item_name": salesOrderItemList[i].product_display_name,
                  "sku_price": salesOrderItemList[i].product_price,
                  "num_user": salesOrderItemList[i].product_qty,
                  "idr_usd_rate": salesOrder.so_exchange_rate,
                  "billing_start_date": format(salesOrderItemList[i].product_active_licence_date, 'yyyy-MM-dd HH:mm:ss'),
                  "installment_type": salesOrderItemList[i].billing_payment_period,
                  "total_contract_month": salesOrderItemList[i].contract_long_period,
                  "quotation_tax_rate": 12,
                  "product_discount_price": salesOrderItemList[i].product_discount_price,
                  "product_discount_prorate": salesOrderItemList[i].product_discount_prorate,
                })
            });
          }

          const responseGetTerms = await fetchGetTerms.json();

          if (responseGetTerms.status === 200) {
            terms = [...terms, ...responseGetTerms.data];
          }

          let quotationItemAccounts = await getQuotationItemAccount(salesOrder.quotation_id, salesOrderItemList[i].product_id);
          if (quotationItemAccounts.length === 0) {
            quotationItemAccounts = await getQuotationItemAccount(salesOrder.quotation_id, null);
          }

          let companies = [];
          companies.push({
            "company_id": quotationItemAccounts[0].subcompany_id,
            "company_name": quotationItemAccounts[0].subcompany_name,
            "licence": quotationItemAccounts[0].product_qty,
          });

          iouItems.push({
            "product_id": salesOrderItemList[i].product_id,
            "product_display_name": salesOrderItemList[i].product_display_name,
            "product_display_description": salesOrderItemList[i].product_display_description,
            "product_type": salesOrderItemList[i].product_type,
            "product_domain": salesOrderItemList[i].product_domain,
            "product_qty": parseFloat(salesOrderItemList[i].product_qty),
            "product_currency_original": salesOrderItemList[i].product_currency_original,
            "product_currency": salesOrderItemList[i].product_currency,
            "product_cost_original": parseFloat(salesOrderItemList[i].product_cost_original),
            "product_cost": parseFloat(salesOrderItemList[i].product_cost),
            "product_price_original": parseFloat(salesOrderItemList[i].product_price_original),
            "product_price": parseFloat(salesOrderItemList[i].product_price),
            "product_subtotal": parseFloat(salesOrderItemList[i].product_subtotal),
            "product_discount_rate": parseFloat(salesOrderItemList[i].product_discount_rate),
            "product_discount_price": parseFloat(salesOrderItemList[i].product_discount_price),
            "product_discount_prorate": parseFloat(salesOrderItemList[i].product_discount_prorate),
            "product_total": parseFloat(salesOrderItemList[i].product_total),
            "product_grand_total": parseFloat(salesOrderItemList[i].product_grand_total),
            "product_active_licence_date": format(new Date(activationDate), 'yyyy-MM-dd'),
            "service_type": salesOrderItemList[i].service_type,
            "contract_long_period": salesOrderItemList[i].contract_long_period,
            "contract_duration_period": salesOrderItemList[i].contract_duration_period,
            "companies": companies,
          })
        }

        let payments = null;

        if (salesOrderItemList[0].billing_payment_period === 'Custom') {

          let termPayments = [];
          let productCount = [];

          const quotationPaymentSimulations = await getQuotationPaymentSimulation(salesOrder.quotation_id);
          for (let j in quotationPaymentSimulations) {
            let payment = quotationPaymentSimulations[j];
            if (!termPayments[payment.term_number]) {
              termPayments[payment.term_number] = {
                term_name: payment.term_number,
                term_period: format(payment.start_term_period, 'd MMM yyyy') + ' - ' + format(new Date(payment.end_term_period), 'd MMM yyyy'),
                number_of_months: payment.number_of_months,
                payment_rate: payment.payment_rate,
              };
            }
            if (!(productCount.includes(payment.quotation_item_id))) {
              productCount.push(payment.quotation_item_id);
            }
            let productIndex = productCount.indexOf(payment.quotation_item_id);

            termPayments[payment.term_number][`item_${(productIndex + 1)}_payment_amount`] = parseFloat(payment.payment_amount_exc_tax);
            if (!termPayments[payment.term_number].total_payment_amount) {
              termPayments[payment.term_number].total_payment_amount = 0;
            }
            termPayments[payment.term_number].total_payment_amount += parseFloat(payment.payment_amount_exc_tax);
          }

          payments = Object.values(termPayments).map(term => ({ ...term, total_payment_amount: term.total_payment_amount }));

        } else {
          const uniqueItems = [...new Set(terms.map((item) => item.item_name))];
          const paymentAmountKeys = uniqueItems.map((item, index) => `item_${index + 1}_payment_amount`);

          payments = terms.reduce((acc, curr) => {
            const {
              term_name,
              start_term_period,
              end_term_period,
              number_of_months,
              item_name,
              payment_amount_exc_tax,
            } = curr;

            let termEntry = acc.find((entry) => entry.term_name === term_name);
            if (!termEntry) {
              const startDate = format(new Date(start_term_period), 'dd MMM yyyy');
              const endDate = format(new Date(end_term_period), 'dd MMM yyyy');

              termEntry = {
                term_name,
                number_of_months,
                term_period: `${startDate} - ${endDate}`,
                ...Object.fromEntries(paymentAmountKeys.map((key) => [key, 0])),
                total_payment_amount: 0,
              };
              acc.push(termEntry);
            }
            const itemIndex = uniqueItems.indexOf(item_name);
            if (itemIndex !== -1) {
              const paymentKey = paymentAmountKeys[itemIndex];
              termEntry[paymentKey] = parseFloat((payment_amount_exc_tax).toFixed(3));
            }
            termEntry.total_payment_amount = paymentAmountKeys.reduce((sum, key) => sum + termEntry[key], 0);
            return acc;
          }, []);  
        }

        const requestIou = {
          "order": {
            "so_id": id_so,
            "items": iouItems,
            "idr_usd_rate": salesOrder.so_exchange_rate,
            "billing_start_date": format(activationDate, 'yyyy-MM-dd HH:mm:ss'),
            "installment_type": salesOrderItemList[0].billing_payment_period,
            "total_contract_month": salesOrderItemList[0].contract_long_period,
            "created_on": format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            "created_by": crmUser.user_keyid,
          },
          "terms_simulation": [...payments].sort((a, b) => (a.term_name || '').localeCompare(b.term_name || ''))
        };

        await jwtFetch(config.ENDPOINT + 'create/iou', {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestIou)
        });

        window.location.href = '/quotation/detail/' + salesOrder.quotation_id;
      }
    } catch (error) {
      console.error('Error in editStatusSalesOrder:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingSubmit(false);
    }
  };

  const uploadScreenshot = async () => {
    const formData = new FormData();
    salesOrderScreenshot.forEach((screenshot) => {
      formData.append('files', screenshot);
    });
    try {      
      const responseUploadScreenshot = await jwtFetch(config.ENDPOINT + 'upload/gdrive', {
        method: 'POST',
        body: formData,
      });
      if (!responseUploadScreenshot.ok) {
        throw new Error(`Upload failed with status: ${responseUploadScreenshot.status}`);
      }
      const resultUploadScreenshot = await responseUploadScreenshot.json();
      if (resultUploadScreenshot.status === 200) {
        return resultUploadScreenshot.data;
      } else {
        alert(`Upload error: ${resultUploadScreenshot.message}`);
        setSalesOrderScreenshot(null);
      }
    } catch (error) {
      alert(`Upload error: ${error.message || error}`);
      setSalesOrderScreenshot(null);
    }
  };

  return (
    <div id="salesorder-detail">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar"/></a>
            </div>
            <hr className="m-0"/>
            <Menu active="salesorder"/>
          </div>
          <div className="col-md-10">
            <Header/>
            <hr className="m-0"/>
            <div className="main">
              <div className="p-4">
                {salesOrder && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div className="fs-24"><button className="btn btn-link" onClick={() => navigate(-1)}><i className="bi bi-arrow-left"/></button> Sales Order Detail</div>
                        {(
                          () => {
                            const statusStyles = {
                              'In Progress': 'secondary',
                              'Completed': 'success', 
                              'Canceled': 'danger'
                            };
                            const style = statusStyles[salesOrder.so_status];
                            return style && (
                              <span className={"badge badge-sm ms-3 text-bg-" + style}>{salesOrder.so_status}</span>
                            );
                          }
                        )()}
                      </div>
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        {salesOrder.so_status === 'In Progress' && crmUser.user_level === 'Admin' && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => cancelStatus('Canceled')} disabled={isLoadingSubmit}><i className="bi bi-x-circle me-2"/> Cancel</button>
                        )}
                        {salesOrder.so_status === 'In Progress' && (
                          !isEditing && isEditable && (
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsEditing(!isEditing)}><i className="bi bi-pencil me-2"/> Edit</button>
                          )
                        )}
                        {salesOrder.so_status === 'In Progress' && (
                          !isEditing && salesOrderItemList && salesOrderItemList.every(item => item.status === 'Completed') && (
                            <button className="btn btn-sm btn-success" onClick={() => editStatusSalesOrder('Completed')} disabled={isLoadingSubmit}><i className="bi bi-check-lg me-2"/>  {isLoadingSubmit ? 'Loading...' : 'Complete'}</button>
                          )
                        )}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Number</div>
                          <div className="col-md-8">{salesOrder.so_code} <i className="bi bi-copy pointer" onClick={() => navigator.clipboard.writeText(salesOrder.so_code)}/></div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Company Group</div>
                          <div className="col-md-8">{salesOrder.company_name} <a href={`/client/detail/${salesOrder.company_id}`}><i className="bi bi-link-45deg"/></a></div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Subsidiary</div>
                          <div className="col-md-8">{salesOrder.subcompany_name} <a href={`/client/subsidiary/detail/${salesOrder.subcompany_id}`}><i className="bi bi-link-45deg"/></a></div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Currency</div>
                          <div className="col-md-8">{salesOrder.so_currency}</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Exchange Rate</div>
                          <div className="col-md-8">1 USD <i className="bi bi-arrow-right mx-1"/> {priceFormat(salesOrder.so_exchange_rate, salesOrder.so_currency)} ({quotation && quotation.quotation_exchange_floating})</div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Created Date</div>
                          <div className="col-md-8">{format(salesOrder.created_on, 'd MMMM yyyy')}</div>
                        </div>
                        {quotation && (
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Order Type</div>
                            <div className="col-md-8">{quotation.quotation_item_list[0].service_type}</div>
                          </div>
                        )}
                        {quotation && (
                          <div className="row mb-2">
                            <div className="col-md-4 text-secondary">Sales Name</div>
                            <div className="col-md-8">{quotation.sales_name}</div>
                          </div>
                        )}
                      </div>
                      <div className="col-md-6">
                        {quotation && (
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between">
                                <p className="fs-14 mb-0"><strong>Quotation</strong></p>
                                <a href={"/quotation/detail/" + quotation.id_quotation}>{quotation.quotation_code} <i className="bi bi-link-45deg ms-1"/></a>
                              </div>
                            </div>
                          </div>
                        )}
                        {quotation && (
                          <div className="card mb-2">
                            <div className="card-body">
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <p className="fs-14 mb-0"><strong>Cost &amp; Profit Summary</strong></p>
                              </div>
                              <div className="p-2">
                                <div className="row">
                                  <div className="col-md-6 text-secondary">Total Cost</div>
                                  <div className="col-md-6 text-end">{priceFormat(quotation.quotation_cost, quotation.quotation_currency)}</div>
                                </div>
                                <hr/>
                                <div className="row mb-2">
                                  <div className="col-md-6 text-secondary">Subtotal Sell (Pre-Discount, Pre-Tax)</div>
                                  <div className="col-md-6 text-end">{priceFormat(quotation.quotation_subtotal, quotation.quotation_currency)}</div>
                                </div>
                                <div className="row mb-2">
                                  <div className="col-md-6 text-secondary">Discount on QT {quotation.quotation_discount_price > 0 ? (" (" + priceFormat(quotation.quotation_discount_rate) + "%)") : ""}</div>
                                  <div className="col-md-6 text-end"><span className="text-danger">{quotation.quotation_discount_price > 0 ? ("-" + priceFormat(quotation.quotation_discount_price, quotation.quotation_currency)) : "0"}</span></div>
                                </div>
                                <div className="row">
                                  <div className="col-md-6 text-secondary">Net Amount Before Tax</div>
                                  <div className="col-md-6 text-end">{priceFormat(quotation.quotation_total, quotation.quotation_currency)}</div>
                                </div>
                                <hr/>
                                <div className="row">
                                  <div className="col-md-6 text-secondary">Estimated  Gross Profit <strong>{" (" + quotation.quotation_margin_rate + "%)"}</strong></div>
                                  <div className="col-md-6 text-end">{priceFormat(quotation.quotation_margin_price, quotation.quotation_currency)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {quotation && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">SKU List</div>
                    </div>
                    <div className="bg-white border-radius-4 p-4">
                      <div className="table-responsive">
                        <table className="table table-hover bordered mb-0">
                          <thead>
                            <tr>
                              <th className="bg-gold w-30">#</th>
                              <th className="bg-gold">SKUs</th>
                              <th className="bg-gold w-85">Period</th>
                              <th className="bg-gold w-85">Qty</th>
                              <th className="bg-gold w-105">Cost Price / Unit</th>
                              <th className="bg-gold w-105">Sell Price / Unit</th>
                              <th className="bg-gold w-105">Discount</th>
                              <th className="bg-gold w-125">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotation.quotation_item_list && quotation.quotation_item_list.map((item, i) => {
                              const iouItem = iouList?.[0]?.iou_items?.find(iouItem => iouItem.product_id === quotation.quotation_item_list[i].product_id);
                              return (
                                <tr className={item.product_total === 0 ? 'd-none' : ''} key={i}>
                                  <td valign="middle" align="center">{i + 1}</td>
                                  <td valign="middle">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                      <p className="mb-0"><strong>{item.product_display_name}</strong></p>
                                      <button className="btn btn-sm btn-outline-secondary" onClick={() => copyItem(i)}><i className="bi bi-copy"/></button>
                                    </div>
                                    {item.product_display_description && item.product_display_description.split('\n').map((description, d) => (
                                      <div className="row mb-1" key={"description-" + d}>
                                        {description.split(':').length > 1 ? (
                                          <div className="col-4">{description.split(':')[0].trim()}</div>
                                        ) : (
                                          <div className="col-12">{description.trim()}</div>
                                        )}
                                        {description.split(':').length > 1 && (
                                          <div className="col-8">
                                            <div className="d-flex">
                                              <span className="me-2">:</span>
                                              <div>{description.split(':').slice(1).join(':').trim()}</div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    {iouItem && iouItem.product_type === 'Subscription' && (
                                      <div className="text-secondary">
                                        <hr/>
                                        {iouItem.service_type === 'Additional' && (
                                          <div className="row mb-1">
                                            <div className="col-4">Period - Current Active Subscription</div>
                                            <div className="col-8">
                                              <div className="d-flex">
                                                <span className="me-2">:</span>
                                                <div>{iouItem.licence_start_period ? (format(new Date(iouItem.licence_start_period), 'd MMMM yyyy') + " - " + format(new Date(iouItem.licence_end_period), 'd MMMM yyyy')) : ''}</div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        <div className="row mb-1">
                                          <div className="col-4">Period - for this QT Order</div>
                                          <div className="col-8">
                                            <div className="d-flex">
                                              <span className="me-2">:</span>
                                              <div>{format(new Date(iouItem.start_term_period), 'd MMMM yyyy') + " - " + format(new Date(iouItem.end_term_period), 'd MMMM yyyy')}</div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </td>
                                  <td valign="middle" align="center">
                                    {
                                      item.product_type === 'Subscription' ? (
                                        (item.contract_long_period + " " + (item.contract_long_period > 1 ? ' Months' : ' Month'))
                                      ) : 'N/A'
                                    }
                                  </td>
                                  <td valign="middle" align="center">{item.product_qty + ' Unit(s)'}</td>
                                  <td valign="middle" align="center">
                                    <div className="d-flex flex-column justify-content-between py-3">
                                      <span>{priceFormat(item.product_cost, item.product_currency)}</span>
                                      {item.product_currency !== item.product_currency_original && (<hr className="my-3"/>)}
                                      {item.product_currency !== item.product_currency_original && (<span className="text-secondary">{priceFormat(item.product_cost_original, item.product_currency_original)}</span>)}
                                    </div>
                                  </td>
                                  <td valign="middle" align="center">
                                    <div className="d-flex flex-column justify-content-between py-3">
                                      <span>{priceFormat(item.product_price, item.product_currency)}</span>
                                      {item.product_currency !== item.product_currency_original && (<hr className="my-3"/>)}
                                      {item.product_currency !== item.product_currency_original && (<span className="text-secondary">{priceFormat(item.product_price_original, item.product_currency_original)}</span>)}
                                    </div>
                                  </td>
                                  <td valign="middle" align="center">
                                    {item.product_discount_price > 0 ? (
                                      <div className="d-flex flex-column justify-content-between py-3">
                                        <span className="text-danger">-{priceFormat(item.product_discount_price, item.product_currency)}</span>
                                        {item.product_discount_price > 0 && (<hr className="my-3"/>)}
                                        {item.product_discount_price > 0 && (<span className="text-secondary">{priceFormat(item.product_discount_rate, "%")}</span>)}
                                      </div>
                                    ) : 'N/A'}
                                  </td>
                                  <td valign="middle" align="end">
                                    <span>{priceFormat(item.product_total, item.product_currency)}</span>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td className="text-end" colSpan={7}>
                                Subtotal
                                <br/>
                                Discount on QT {quotation.quotation_discount_price > 0 ? (" (" + priceFormat(quotation.quotation_discount_rate) + "%)") : ""}
                                <br/>
                                Total Before Tax
                              </td>
                              <td className="text-end">
                                {priceFormat(quotation.quotation_subtotal, quotation.quotation_currency)}
                                <br/>
                                <span className="text-danger">{quotation.quotation_discount_price > 0 ? ("-" + priceFormat(quotation.quotation_discount_price, quotation.quotation_currency)) : "0"}</span>
                                <br/>
                                <strong>{priceFormat(quotation.quotation_total, quotation.quotation_currency)}</strong>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {quotation && paymentSimulations && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Payment Schedules</div>
                    </div>
                    <div className="bg-white border-radius-4 p-4">
                      <div className="table-responsive">
                        <table className="table table-hover bordered mb-0">
                          <thead>
                            <tr>
                              <th className="bg-gold w-90">Schedule</th>
                              <th className="bg-gold w-200">Date</th>
                              <th className="bg-gold">SKUs</th>
                              <th className="bg-gold w-125">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quotation.quotation_items && (
                              (() => {
                                const termGroups = {};
                                quotation.quotation_items.forEach((item) => {
                                  item.payment_simulations.forEach((payment) => {
                                    const key = `${payment.term_number}-${payment.number_of_months}`;
                                    if (!termGroups[key]) {
                                      termGroups[key] = {
                                        term_number: payment.term_number,
                                        number_of_months: payment.number_of_months,
                                        start_term_period: payment.start_term_period,
                                        end_term_period: payment.end_term_period,
                                        items: [],
                                      };
                                    }
                                    termGroups[key].items.push({
                                      product_display_name: item.product_display_name,
                                      payment_amount_exc_tax: payment.payment_amount_exc_tax,
                                    });
                                  });
                                });
                                const sortedTerms = Object.values(termGroups).sort((a, b) => a.term_number - b.term_number);        
                                return sortedTerms.map((term, termIndex) => {
                                  const rowspan = term.items.length;
                                  return term.items.map((item, itemIndex) => (
                                    <tr key={`term-${termIndex}-item-${itemIndex}`}>
                                      {itemIndex === 0 && (
                                        <>
                                          <td rowSpan={rowspan} valign="middle" align="center">
                                            {term.term_number.replace('Term', 'Schedule')}
                                          </td>
                                          <td rowSpan={rowspan} valign="middle" align="center">
                                            <span>
                                              {(format(new Date(term.start_term_period), 'd MMM yyyy')) + " - " + (format(new Date(term.end_term_period), 'd MMM yyyy'))}
                                            </span>
                                          </td>
                                        </>
                                      )}
                                      <td>{item.product_display_name}</td>
                                      <td style={{ textAlign: 'right' }}>
                                        {priceFormat(item.payment_amount_exc_tax, quotation.quotation_currency)}
                                      </td>
                                    </tr>
                                  ));
                                }).flat();
                              })()
                            )}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td className="text-end" colSpan={3}>Total Before Tax</td>
                              <td className="text-end">
                                <strong>{priceFormat(quotation.quotation_total, quotation.quotation_currency)}</strong>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {quotation && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">Attachments</div>
                    </div>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="row align-items-top form-group mb-2">
                          <div className="col-md-2">
                            <label>Files</label>
                          </div>
                          <div className="col-md-10">
                            {JSON.parse(quotation.attachment_files) && (
                              <div className="table-responsive">
                                <table className="table table-hover bordered mb-2">
                                  <thead>
                                    <tr>
                                      <th className="bg-gold w-30">#</th>
                                      <th className="bg-gold text-start">Name</th>
                                      <th className="bg-gold text-start">File</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {JSON.parse(quotation.attachment_files).map((attachments, a) => 
                                      <tr key={"attachment-" + a}>
                                        <td valign="middle" align="center">{a + 1}</td>
                                        <td valign="middle">{attachments.name}</td>
                                        <td valign="middle">
                                          {Array.isArray(attachments.files) && attachments.files.length > 0 && (
                                            <a className="ellipsis" href={attachments.files[0].fileUrl} target="_blank" rel="noopener noreferrer">{shortenUrl(attachments.files[0].fileUrl, 50)} <i className="bi bi-link-45deg ms-2"/></a>
                                          )}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Quotation Remarks</label>
                          </div>
                          <div className="col-md-10">{quotation.quotation_remarks}</div>
                        </div>
                        {salesOrder && (
                          <div className="row align-items-baseline form-group mb-2">
                            <div className="col-md-2">
                              <label>Sales Order Remarks</label>
                            </div>
                            <div className="col-md-10">{salesOrder.so_remarks}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {salesOrder && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-24">{salesOrderType === 'Subscription' ? 'Subscription' : 'Delivery'}</div>
                    </div>
                    {salesOrderType === 'Subscription' ? (
                      <div className="row">
                        <div className="col-md-6">
                          <form id="edit-activation" onSubmit={(e) => editActivationDate(e)}>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Licence Start Date {isEditing && (<span className="text-danger">*</span>)}</label>
                              </div>
                              <div className="col-md-8">
                                {!isEditing ? format(new Date(activationDate), 'dd MMMM yyyy') : (
                                  <input className="form-control" type="date" value={activationDate} onChange={(e) => setActivationDate(e.target.value)} required/>
                                )}
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Customer ID {isEditing && (<span className="text-danger">*</span>)}</label>
                              </div>
                              <div className="col-md-8">
                                {!isEditing ? salesOrderCustomerId : (
                                  <input className="form-control" type="text" value={salesOrderCustomerId} onChange={(e) => setSalesOrderCustomerId(e.target.value)} required/>
                                )}
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Domain for Provisioning {isEditing && (<span className="text-danger">*</span>)}</label>
                              </div>
                              <div className="col-md-8">
                                {!isEditing ? salesOrderDomain : (
                                  <input className="form-control" type="text" value={salesOrderDomain} onChange={(e) => setSalesOrderDomain(e.target.value)} required/>
                                )}
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Proof of Provisioning {isEditing && (<span className="text-danger">*</span>)}</label>
                              </div>
                              <div className="col-md-8">
                                <div className="d-flex">
                                  {!isEditing && salesOrder.so_screenshot ? (
                                    JSON.parse(salesOrder.so_screenshot).map((screenshot, s) => 
                                      <iframe src={screenshot.fileUrl} width="240" height="160" title={"Sales order screenshot " + (s + 1)} key={"files-" + s}/>
                                    )
                                  ) : (
                                    <input className="form-control" type="file" multiple={true} accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => setSalesOrderScreenshot(Array.from(e.target.files))} required/>
                                  )}
                                </div>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-md-6">
                          <form id="edit-activation" onSubmit={(e) => editActivationDate(e)}>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Delivery Date {isEditing && (<span className="text-danger">*</span>)}</label>
                              </div>
                              <div className="col-md-8">
                                {!isEditing ? format(new Date(activationDate), 'dd MMMM yyyy') : (
                                  <input className="form-control" type="date" value={activationDate} onChange={(e) => setActivationDate(e.target.value)} required/>
                                )}
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Proof of Delivery</label>
                              </div>
                              <div className="col-md-8">
                                <div className="d-flex">
                                  {!isEditing && salesOrder.so_screenshot ? (
                                    JSON.parse(salesOrder.so_screenshot).map((screenshot, s) => 
                                      <iframe src={screenshot.fileUrl} width="240" height="160" title={"Sales order screenshot " + (s + 1)} key={"files-" + s}/>
                                    )
                                  ) : (
                                    <input className="form-control" type="file" multiple={true} accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => setSalesOrderScreenshot(Array.from(e.target.files))}/>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="row align-items-baseline form-group mb-2">
                              <div className="col-md-4">
                                <label>Remarks</label>
                              </div>
                              <div className="col-md-8">
                                {!isEditing ? salesOrder.so_notes : (
                                  <textarea className="form-control" rows={4} value={salesOrderNotes} placeholder="Additional information..." onChange={(e) => setSalesOrderNotes(e.target.value)}/>
                                )}
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {salesOrder && isEditing && salesOrder.so_status !== 'Completed' && (
                  activationDate ? (
                    <div className="mb-4">
                      <div className="d-flex justify-content-end">
                        <button className="btn btn-link" type="button" onClick={() => window.location.reload() /*setIsEditing(false)*/}>Cancel</button>
                        <button className="btn btn-primary ms-2" type="submit" form="edit-activation" disabled={isLoadingSubmit}>{isLoadingSubmit ? 'Loading...' : 'Update'}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="d-flex justify-content-end">
                        <button className="btn btn-primary ms-2" type="submit" form="edit-activation" disabled={isLoadingSubmit}>{isLoadingSubmit ? 'Loading...' : 'Save'}</button>
                      </div>
                    </div>
                  )
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesOrderDetail;


